import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styled, { useTheme } from 'styled-components'
import PropTypes from 'prop-types'

const TooltipPortal = styled.div`
  position: fixed;
  background-color: ${props => props.theme.color.primaryText};
  color: ${props => props.theme.color.bg};
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  opacity: ${props => (props.visible ? 1 : 0)};
  visibility: ${props => (props.visible ? 'visible' : 'hidden')};
  transform: ${props => {
    const scale = props.visible ? 'scale(1)' : 'scale(0.8)'
    const translateX = props.centered ? 'translateX(-50%)' : 'translateX(0)'
    return `${translateX} ${scale}`
  }};
  transform-origin: ${props => (props.centered ? 'center top' : 'left top')};
  transition: opacity 0.15s ease-out, visibility 0.15s ease-out,
    transform 0.15s ease-out;
  z-index: 10000;
  pointer-events: none;
`

const Tooltip = ({
  children,
  text,
  triggerRef,
  placement = 'bottom',
  offset = 8,
  disabled = false
}) => {
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const [centered, setCentered] = useState(false)
  const theme = useTheme()

  const calculatePosition = triggerRect => {
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const estimatedTooltipHeight = 32

    let top, left
    let shouldCenter = false

    if (placement === 'bottom') {
      top = triggerRect.bottom + offset
    } else if (placement === 'top') {
      top = triggerRect.top - estimatedTooltipHeight - offset
    } else if (placement === 'left') {
      top =
        triggerRect.top + triggerRect.height / 2 - estimatedTooltipHeight / 2
      left = triggerRect.left - offset
    } else if (placement === 'right') {
      top =
        triggerRect.top + triggerRect.height / 2 - estimatedTooltipHeight / 2
      left = triggerRect.right + offset
    }

    if (placement === 'top' || placement === 'bottom') {
      const triggerCenter = triggerRect.left + triggerRect.width / 2
      left = triggerCenter
      shouldCenter = true

      // Check if centered tooltip would go off screen
      const estimatedTooltipWidth = text.length * 8 + 24
      const leftEdge = triggerCenter - estimatedTooltipWidth / 2
      const rightEdge = triggerCenter + estimatedTooltipWidth / 2

      if (leftEdge < 10) {
        left = 10 + estimatedTooltipWidth / 2
      } else if (rightEdge > viewportWidth - 10) {
        left = viewportWidth - 10 - estimatedTooltipWidth / 2
      }
    }

    if (
      top + estimatedTooltipHeight > viewportHeight - 10 &&
      placement === 'bottom'
    ) {
      const spaceAbove = triggerRect.top
      const spaceBelow = viewportHeight - triggerRect.bottom
      if (spaceAbove > spaceBelow) {
        top = triggerRect.top - estimatedTooltipHeight - offset
      }
    }

    if (top < 10 && placement === 'top') {
      const spaceAbove = triggerRect.top
      const spaceBelow = viewportHeight - triggerRect.bottom
      if (spaceBelow > spaceAbove) {
        top = triggerRect.bottom + offset
      }
    }

    return {
      top,
      left,
      centered: shouldCenter
    }
  }

  useEffect(() => {
    const element = triggerRef?.current
    if (!element || disabled) return

    const handleMouseEnter = () => {
      const rect = element.getBoundingClientRect()
      const { top, left, centered } = calculatePosition(rect)

      setPosition({ top, left })
      setCentered(centered)
      setVisible(true)
    }

    const handleMouseLeave = () => {
      setVisible(false)
    }

    element.addEventListener('mouseenter', handleMouseEnter)
    element.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter)
      element.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [triggerRef, text, placement, offset, disabled])

  if (disabled || !text) {
    return children
  }

  return (
    <>
      {children}
      {createPortal(
        <TooltipPortal
          visible={visible}
          centered={centered}
          style={{
            top: position.top,
            left: position.left
          }}
          theme={theme}
        >
          {text}
        </TooltipPortal>,
        document.body
      )}
    </>
  )
}

Tooltip.propTypes = {
  children: PropTypes.node.isRequired,
  text: PropTypes.string.isRequired,
  triggerRef: PropTypes.object.isRequired,
  placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  offset: PropTypes.number,
  disabled: PropTypes.bool
}

export default Tooltip
